(function () {
  'use strict';

  const SELECTOR_FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  let lastFocused = null;

  function drawer() {
    return document.querySelector('[data-peregy-drawer]');
  }

  function openDrawer() {
    const element = drawer();
    if (!element) return;
    lastFocused = document.activeElement;
    element.classList.add('is-open');
    element.setAttribute('aria-hidden', 'false');
    document.body.classList.add('peregy-lock');
    const first = element.querySelector(SELECTOR_FOCUSABLE);
    window.requestAnimationFrame(() => first?.focus());
  }

  function closeDrawer() {
    const element = drawer();
    if (!element) return;
    element.classList.remove('is-open');
    element.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('peregy-lock');
    if (lastFocused instanceof HTMLElement) lastFocused.focus();
  }

  function switchDrawerTab(button) {
    const element = drawer();
    if (!element) return;
    const name = button.dataset.peregyDrawerTab;
    element.querySelectorAll('[data-peregy-drawer-tab]').forEach((tab) => {
      const active = tab === button;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
    });
    element.querySelectorAll('[data-peregy-drawer-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.peregyDrawerPanel !== name;
    });
  }

  function switchProductTab(button) {
    const root = button.closest('[data-peregy-tabs]');
    if (!root) return;
    const name = button.dataset.peregyTab;
    root.querySelectorAll('[data-peregy-tab]').forEach((tab) => {
      const active = tab === button;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    root.querySelectorAll('[data-peregy-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.peregyPanel !== name;
    });
  }

  function switchDiscoveryTab(button) {
    const root = button.closest('[data-peregy-discovery]');
    if (!root) return;
    const name = button.dataset.peregyDiscoveryTab;
    root.querySelectorAll('[data-peregy-discovery-tab]').forEach((tab) => {
      const active = tab === button;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    root.querySelectorAll('[data-peregy-discovery-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.peregyDiscoveryPanel !== name;
    });
  }

  function initBrandDirectory(root) {
    root.querySelectorAll('[data-peregy-brand-directory]').forEach((directory) => {
      const input = directory.querySelector('[data-peregy-brand-search]');
      if (!input || input.dataset.peregyReady === 'true') return;
      input.dataset.peregyReady = 'true';
      input.addEventListener('input', () => {
        const query = input.value.trim().toLocaleLowerCase('ar');
        let visible = 0;
        directory.querySelectorAll('[data-peregy-brand-name]').forEach((item) => {
          const match = !query || item.dataset.peregyBrandName.includes(query);
          item.hidden = !match;
          if (match) visible += 1;
        });
        const empty = directory.querySelector('[data-peregy-brand-empty]');
        if (empty) empty.hidden = visible > 0;
      });
    });
  }

  function track(element) {
    if (!element) return;
    const detail = {
      action: element.dataset.peregyEvent,
      label: element.dataset.peregyLabel || '',
      path: window.location.pathname
    };
    document.dispatchEvent(new CustomEvent('peregy:track', { detail }));
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: `peregy_${detail.action}`, peregy_label: detail.label, peregy_path: detail.path });
    }
    try {
      window.Shopify?.analytics?.publish?.(`peregy_${detail.action}`, detail);
    } catch (error) {
      /* Analytics availability must never block commerce interactions. */
    }
  }

  function rotateBrands(root) {
    if (root.dataset.rotateDaily !== 'true' || root.dataset.rotated === 'true') return;
    const cards = Array.from(root.children);
    if (cards.length < 2) return;
    const today = new Date();
    const dayKey = Math.floor(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()) / 86400000);
    const offset = dayKey % cards.length;
    cards.slice(offset).concat(cards.slice(0, offset)).forEach((card) => root.appendChild(card));
    root.dataset.rotated = 'true';
  }

  function trapFocus(event) {
    const element = drawer();
    if (!element?.classList.contains('is-open') || event.key !== 'Tab') return;
    const items = Array.from(element.querySelectorAll(SELECTOR_FOCUSABLE)).filter((item) => item.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function syncCartTrigger() {
    const trigger = document.querySelector('[data-peregy-cart-drawer]');
    const cartDrawer = document.getElementById('cart-drawer');
    if (trigger) trigger.setAttribute('aria-expanded', String(Boolean(cartDrawer?.hasAttribute('open'))));
  }

  async function openCartDrawer() {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!(cartDrawer instanceof HTMLElement)) return false;

    if (typeof cartDrawer.open !== 'function' && window.customElements?.whenDefined) {
      try {
        await Promise.race([
          customElements.whenDefined('theme-drawer'),
          new Promise((resolve) => window.setTimeout(resolve, 900)),
        ]);
      } catch (error) { /* fall through */ }
    }

    if (typeof cartDrawer.open === 'function') {
      cartDrawer.open();
      return true;
    }

    const dialog = cartDrawer.querySelector('dialog');
    if (dialog instanceof HTMLDialogElement) {
      if (!dialog.open) {
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      }
      cartDrawer.setAttribute('open', '');
      return true;
    }
    return false;
  }

  let quickViewLastFocused = null;

  function ensurePeregyQuickViewDialog() {
    let dialog = document.querySelector('[data-peregy-quick-view-dialog]');
    if (dialog instanceof HTMLDialogElement) return dialog;

    dialog = document.createElement('dialog');
    dialog.className = 'peregy-quick-view-dialog';
    dialog.setAttribute('data-peregy-quick-view-dialog', '');
    dialog.setAttribute('aria-label', 'اختيار نوع المنتج');
    dialog.innerHTML = `
      <div class="peregy-quick-view__surface">
        <button class="peregy-quick-view__close" type="button" data-peregy-qv-close aria-label="إغلاق">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>
        </button>
        <div data-peregy-qv-host></div>
      </div>`;

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closePeregyQuickView();
    });

    dialog.addEventListener('close', () => {
      const host = dialog.querySelector('[data-peregy-qv-host]');
      if (host) host.replaceChildren();
      document.body.classList.remove('peregy-quick-view-open');
      if (quickViewLastFocused instanceof HTMLElement && quickViewLastFocused.isConnected) {
        quickViewLastFocused.focus({ preventScroll: true });
      }
      quickViewLastFocused = null;
    });

    document.body.appendChild(dialog);
    return dialog;
  }

  function openPeregyQuickView(trigger) {
    if (!(trigger instanceof HTMLElement)) return;
    const card = trigger.closest('.peregy-product-card');
    const template = card?.querySelector('template[data-peregy-quick-view-template]');
    if (!(template instanceof HTMLTemplateElement)) {
      const fallbackUrl = card instanceof HTMLElement ? card.dataset.peregyProductUrl : '';
      if (fallbackUrl) window.location.href = fallbackUrl;
      return;
    }

    const dialog = ensurePeregyQuickViewDialog();
    const host = dialog.querySelector('[data-peregy-qv-host]');
    if (!host) return;

    quickViewLastFocused = trigger;
    host.replaceChildren(template.content.cloneNode(true));
    document.body.classList.add('peregy-quick-view-open');

    if (!dialog.open) dialog.showModal();
    window.requestAnimationFrame(() => dialog.querySelector('[data-peregy-qv-variant]:not([disabled])')?.focus());
  }

  function closePeregyQuickView() {
    const dialog = document.querySelector('[data-peregy-quick-view-dialog]');
    if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
  }

  function selectPeregyQuickViewVariant(button) {
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    const content = button.closest('[data-peregy-qv-content]');
    if (!(content instanceof HTMLElement)) return;

    content.querySelectorAll('[data-peregy-qv-variant]').forEach((option) => {
      const selected = option === button;
      option.classList.toggle('is-selected', selected);
      option.setAttribute('aria-checked', String(selected));
    });

    const variantInput = content.querySelector('[data-peregy-qv-variant-id]');
    if (variantInput instanceof HTMLInputElement) variantInput.value = button.dataset.variantId || '';

    const price = content.querySelector('[data-peregy-qv-price]');
    if (price) price.textContent = button.dataset.price || '';

    const compare = content.querySelector('[data-peregy-qv-compare]');
    if (compare instanceof HTMLElement) {
      const compareValue = button.dataset.compare || '';
      compare.textContent = compareValue;
      compare.hidden = !compareValue;
    }

    const selection = content.querySelector('[data-peregy-qv-selection]');
    if (selection) selection.textContent = button.dataset.variantTitle || '';

    const image = content.querySelector('[data-peregy-qv-image]');
    if (image instanceof HTMLImageElement && button.dataset.image) image.src = button.dataset.image;

    const submit = content.querySelector('[data-peregy-qv-submit]');
    if (submit instanceof HTMLButtonElement) {
      submit.disabled = false;
      const submitLabel = submit.querySelector('[data-peregy-qv-submit-label]');
      if (submitLabel) submitLabel.textContent = submit.dataset.readyLabel || 'أضف للعربة';
    }
  }

  function onQuickViewCapture(event) {
    const openTrigger = event.target.closest?.('[data-peregy-quick-view]');
    if (openTrigger) {
      event.preventDefault();
      event.stopImmediatePropagation();
      openPeregyQuickView(openTrigger);
      return;
    }

    const closeTrigger = event.target.closest?.('[data-peregy-qv-close]');
    if (closeTrigger) {
      event.preventDefault();
      closePeregyQuickView();
      return;
    }

    const variantTrigger = event.target.closest?.('[data-peregy-qv-variant]');
    if (variantTrigger) {
      event.preventDefault();
      selectPeregyQuickViewVariant(variantTrigger);
    }
  }


  function onClick(event) {
    const searchTrigger = event.target.closest('[data-peregy-search-modal]');
    if (searchTrigger) {
      const searchModal = document.getElementById('search-modal');
      if (typeof searchModal?.showDialog === 'function') {
        event.preventDefault();
        searchModal.showDialog();
        track(searchTrigger);
        return;
      }
    }

    const cartTrigger = event.target.closest('[data-peregy-cart-drawer]');
    if (cartTrigger) {
      event.preventDefault();
      openCartDrawer().then((opened) => {
        if (!opened) window.location.href = cartTrigger.getAttribute('href') || '/cart';
      });
      track(cartTrigger);
      return;
    }

    const open = event.target.closest('[data-peregy-open-drawer]');
    if (open) { event.preventDefault(); openDrawer(); return; }
    const close = event.target.closest('[data-peregy-close-drawer]');
    if (close) { event.preventDefault(); closeDrawer(); return; }
    const drawerTab = event.target.closest('[data-peregy-drawer-tab]');
    if (drawerTab) { switchDrawerTab(drawerTab); return; }
    const productTab = event.target.closest('[data-peregy-tab]');
    if (productTab) { switchProductTab(productTab); return; }
    const discoveryTab = event.target.closest('[data-peregy-discovery-tab]');
    if (discoveryTab) { switchDiscoveryTab(discoveryTab); return; }
    track(event.target.closest('[data-peregy-event]'));
  }

  function onKeydown(event) {
    if (event.key === 'Escape') closeDrawer();
    trapFocus(event);
    const activeTab = event.target.closest?.('[data-peregy-tab], [data-peregy-discovery-tab]');
    if (!activeTab || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    const isDiscovery = activeTab.hasAttribute('data-peregy-discovery-tab');
    const root = activeTab.closest(isDiscovery ? '[data-peregy-discovery]' : '[data-peregy-tabs]');
    const tabs = Array.from(root.querySelectorAll(isDiscovery ? '[data-peregy-discovery-tab]' : '[data-peregy-tab]'));
    const rtl = getComputedStyle(root).direction === 'rtl';
    const direction = event.key === 'ArrowRight' ? (rtl ? -1 : 1) : (rtl ? 1 : -1);
    const next = tabs[(tabs.indexOf(activeTab) + direction + tabs.length) % tabs.length];
    event.preventDefault();
    next.focus();
    if (isDiscovery) switchDiscoveryTab(next);
    else switchProductTab(next);
  }

  function init(root) {
    (root || document).querySelectorAll('[data-peregy-brand-grid]').forEach(rotateBrands);
    initBrandDirectory(root || document);
    syncCartTrigger();
  }

  document.addEventListener('click', onQuickViewCapture, true);
  document.addEventListener('click', onClick);
  document.addEventListener('peregy:quick-view-added', closePeregyQuickView);
  document.addEventListener('theme-drawer:open', (event) => {
    if (event.target?.id === 'cart-drawer') syncCartTrigger();
  });
  document.addEventListener('theme-drawer:close', (event) => {
    if (event.target?.id === 'cart-drawer') syncCartTrigger();
  });
  document.addEventListener('keydown', onKeydown);
  document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
